import {CSSProperties, useCallback, useContext, useEffect, useState} from 'react';
import {shallowEqual} from 'react-redux';
import {EventDesign} from '~/common/components/EventDesign/EventDesign';
import {FabricTemplate} from '~/common/fabric/types';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {FloatingOptionsPanel} from '~/QuickEcards/components/FloatingOptionsPanel/FloatingOptionsPanel';
import {OptionsBottomDrawer} from '~/QuickEcards/components/OptionsBottomDrawer/OptionsBottomDrawer';
import {OptionsPanel} from '~/QuickEcards/components/OptionsPanel/OptionsPanel';
import {useOverlay} from '~/QuickEcards/components/OverlayAnimation/actions/useOverlay';
import {OverlayAnimation} from '~/QuickEcards/components/OverlayAnimation/OverlayAnimation';
import {
  BottomSheetTabContext,
  BottomSheetTabContextParent,
} from '~/QuickEcards/context/BottomSheetTabContext';
import {EcardContext} from '~/QuickEcards/context/EcardContext';
import {useCuratedSets} from '~/QuickEcards/hooks/useCuratedSets';
import {Preview} from '~/QuickEcards/routes/Preview/Preview';
import {useAppDispatch, useAppSelector} from '~/QuickEcards/store';
import {selectAssets} from '~/QuickEcards/store/reducers/assets';
import {
  selectBackground,
  selectEcardId,
  selectFabricArt,
  updateEventData,
  updateEventDesignOptions,
  updateTemplateData,
} from '~/QuickEcards/store/reducers/ecard';
import {logChangeDesign, logDesignStepPreviewClick} from '~/QuickEcards/utils/logging';
import {updateTemplateNameInPathname} from '~/QuickEcards/utils/navigation';
import {Button} from '~library/atoms/Button';
import {Video} from '~library/atoms/Video';
import {TriangleIcon} from '~library/svg/TriangleIcon';

import './QuickEcardDesign.sass';

const QuickEcardDesign = () => {
  const dispatch = useAppDispatch();
  const isDesktop = useIsLargishAndUp();
  const eCardContext = useContext(EcardContext);
  const eCardId = useAppSelector(selectEcardId);
  const fabricArt = useAppSelector(selectFabricArt, shallowEqual);
  const globalAssets = useAppSelector(selectAssets, shallowEqual);
  const background = useAppSelector((state) => selectBackground(state, isDesktop));
  const {tab} = useContext(BottomSheetTabContext);
  const {
    overlayIcons: assets,
    onOverlayChange,
    onOverlayClear,
  } = useOverlay(!isDesktop, tab === 'reveals');
  const {getCuratedSets} = useCuratedSets();
  const [curatedCardTemplates, setCuratedCardTemplates] = useState<FabricTemplate[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const templateAsset = fabricArt?.backgroundImage?.src;
  const isMp4 = templateAsset?.endsWith('.mp4');

  const handleAssetClick = useCallback(
    async (id: string) => {
      if (tab === 'design') {
        const foundCardTemplate = curatedCardTemplates.find((card) => card.id === id);
        if (!foundCardTemplate) return;

        const foundBackgroundInAsset = globalAssets.backgrounds.find(
          (bg) => foundCardTemplate.backgrounds[0] === bg.id
        );

        const backgroundObj = {
          background: foundBackgroundInAsset?.media[2]?.url ?? foundCardTemplate.background_color,
          // biome-ignore lint/style/useNamingConvention: existing error
          background_image_desktop: foundBackgroundInAsset?.media[1]?.url ?? '',
          // biome-ignore lint/style/useNamingConvention: existing error
          background_image_mobile: foundBackgroundInAsset?.media[2]?.url ?? '',
        };

        dispatch(
          updateEventData({
            // biome-ignore lint/style/useNamingConvention: existing error
            fabric_design_data: foundCardTemplate.editable_art.default,
          })
        );
        // biome-ignore lint/style/useNamingConvention: existing error
        dispatch(updateTemplateData({template_name: id, title: foundCardTemplate.display_name}));
        dispatch(updateEventDesignOptions(backgroundObj));
        logChangeDesign(eCardId, id);
        if (!eCardId) {
          // Update document title when selected template changes
          if (foundCardTemplate.display_name.includes('Card')) {
            document.title = `${foundCardTemplate.display_name} | Evite`;
          } else {
            document.title = `${foundCardTemplate.display_name} Card | Evite`;
          }
          // Update URL when selected template changes
          updateTemplateNameInPathname(id);
        }
      } else if (tab) {
        if (id) onOverlayChange(id);
        else onOverlayClear();
      }
    },
    [
      tab,
      eCardId,
      curatedCardTemplates,
      globalAssets.backgrounds,
      dispatch,
      onOverlayChange,
      onOverlayClear,
    ]
  );

  const handlePreviewClick = useCallback(() => {
    logDesignStepPreviewClick(eCardId);
    setShowPreview(true);
  }, [eCardId]);

  useEffect(() => {
    // This appends the newly curated cards to the end of the existing list
    const updateDisplayedCuratedCards = (newData: FabricTemplate[]) => (prev: FabricTemplate[]) => {
      const templates = [...prev];
      (Object.values(newData) as FabricTemplate[]).forEach((ty) => {
        if (!templates.some((template) => template.id === ty.id)) {
          templates.push(ty);
        }
      });
      return templates;
    };

    async function startFetch() {
      const searchParams = new URLSearchParams(window.location.search);
      const category = searchParams.get('c') ?? '';

      const {data} = await getCuratedSets({
        // biome-ignore lint/style/useNamingConvention: existing error
        category_id: category,
        // biome-ignore lint/style/useNamingConvention: existing error
        page_offset: 1,
        // biome-ignore lint/style/useNamingConvention: existing error
        page_size: 40,
      });
      if (!data) {
        // NOTE: If there isn't any curated sets then fallback to 'thankyou_cards'
        const {data: thankyouData} = await getCuratedSets({
          // biome-ignore lint/style/useNamingConvention: existing error
          category_id: 'thankyou_cards',
          // biome-ignore lint/style/useNamingConvention: existing error
          page_size: 40,
          // biome-ignore lint/style/useNamingConvention: existing error
          page_offset: 1,
        });
        if (thankyouData) {
          setCuratedCardTemplates(updateDisplayedCuratedCards(thankyouData as FabricTemplate[]));
        }
        return;
      }

      setCuratedCardTemplates(updateDisplayedCuratedCards(data as FabricTemplate[]));
    }

    startFetch();
  }, [getCuratedSets]);

  if (!fabricArt) return null;

  const style: CSSProperties = {
    background: background ?? undefined,
  };

  return (
    <EventDesign
      selectedBackground={{
        id: '',
        selected: false,
        src: '',
        icon: '',
        isVideo: false,
      }}
      className="quick-ecards-outer-container"
      style={style}
      section={
        <div className="quick-ecards-inner-container">
          <div className="quick-ecards-card-container">
            <Button
              variant="unstyled-button"
              className="ecard__preview-button"
              onClick={handlePreviewClick}
              data-qa-id="preview-animation"
            >
              <TriangleIcon ratio={0.75} />
              Preview
            </Button>
            {isMp4 && templateAsset ? (
              <Video src={templateAsset} className="quick-ecards-img" />
            ) : (
              <img className="quick-ecards-img" src={templateAsset} alt="ecard design" />
            )}
          </div>

          {isDesktop && (
            <FloatingOptionsPanel
              assets={tab === 'design' ? curatedCardTemplates : assets}
              onAssetClick={handleAssetClick}
            />
          )}
          {tab === 'reveals' && (
            <OverlayAnimation autoPlay fixed isReveal revealVideo={eCardContext.revealVideo} />
          )}
          {tab === 'effects' && (
            <OverlayAnimation autoPlay fixed effectVideo={eCardContext.effectVideo} />
          )}
        </div>
      }
      isQuickEcard
      PreviewElement={
        <Preview flipBehavior="disable" showPreviewControls onClose={() => setShowPreview(false)} />
      }
      showPreview={showPreview}
      bottomNav={
        isDesktop ? (
          <OptionsPanel />
        ) : (
          <OptionsBottomDrawer
            tab={tab}
            assets={tab === 'design' ? curatedCardTemplates : assets}
            onAssetClick={handleAssetClick}
          />
        )
      }
    />
  );
};

export const QuickEcardDesignContainer = () => (
  <BottomSheetTabContextParent>
    <QuickEcardDesign />
  </BottomSheetTabContextParent>
);
